import { useMutation } from "@tanstack/react-query";
import { Button, Form, Input } from "antd";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../../api/client";
import type { DeviseStatusResponse } from "./PublicPage";
import { PublicLinks, PublicPage, StatusAlert } from "./PublicPage";

type Flow = "password" | "confirmation" | "unlock";

/** The three Devise endpoints that mail a one-time link. */
const PATHS: Record<Flow, string> = {
  password: "/password",
  confirmation: "/confirmation",
  unlock: "/unlock",
};

const ROUTES: Record<Flow, string> = {
  password: "/users/password/new",
  confirmation: "/users/confirmation/new",
  unlock: "/users/unlock/new",
};

/**
 * "Send me instructions" — /users/password/new, /users/confirmation/new and
 * /users/unlock/new, the three forms the old application rendered from Devise's
 * own views.
 *
 * All three answer the same way whether or not the address is known, so that the
 * endpoint cannot be used to find out which e-mail addresses have an account.
 * There is therefore nothing to report but the message the server sends back.
 */
export function InstructionsPage({ flow }: { flow: Flow }) {
  const { t } = useTranslation();

  const title =
    flow === "password"
      ? t("auth.views.passwords.reset")
      : flow === "confirmation"
        ? t("auth.views.confirmations.resend_instructions")
        : t("auth.views.unlock.resend_instructions");

  const send = useMutation({
    // Devise reads its parameters from params[:user], not from the top level.
    mutationFn: (email: string) =>
      apiFetch<DeviseStatusResponse>(PATHS[flow], {
        method: "POST",
        anonymous: true,
        body: { user: { email } },
      }),
  });

  return (
    <PublicPage title={title}>
      <StatusAlert result={send.data} error={send.error} />

      <Form
        layout="vertical"
        onFinish={(values: { email: string }) => send.mutate(values.email)}
      >
        <Form.Item
          name="email"
          label={t("activerecord.attributes.user.email")}
          rules={[{ required: true, type: "email" }]}
        >
          <Input type="email" autoComplete="username" autoFocus />
        </Form.Item>

        <Button type="primary" htmlType="submit" block loading={send.isPending}>
          {t("form.send")}
        </Button>
      </Form>

      <PublicLinks exclude={ROUTES[flow]} />
    </PublicPage>
  );
}
